/**
 * Part 16 — partial Xero integration failure / safe resume.
 *
 * After document create succeeds, later steps may fail (attachments, history,
 * CN allocation). OMS records per-step state so a retry only re-runs failed
 * steps — never creates another Bill / Credit Note.
 *
 * Legacy docs (xeroInvoiceId set, no xeroExportSteps) are treated as complete
 * so Part 14-era exports are not re-queued.
 */

export const XERO_EXPORT_STEPS = Object.freeze({
  DOCUMENT_CREATED: 'documentCreated',
  SUPPLIER_ATTACHMENTS: 'supplierAttachments',
  AUDIT_ATTACHMENT: 'auditAttachment',
  HISTORY_NOTE: 'historyNote',
  CREDIT_ALLOCATION: 'creditAllocation',
})

export function createEmptyXeroExportSteps() {
  return {
    documentCreated: false,
    supplierAttachments: false,
    auditAttachment: false,
    historyNote: false,
    creditAllocation: false,
    /** True when allocation is not required (no target bill / not a CN). */
    creditAllocationNotApplicable: false,
    complete: false,
    lastErrorStep: null,
    lastError: null,
    updatedAt: null,
  }
}

export function hasExplicitXeroExportSteps(document = {}) {
  return Boolean(document?.xeroExportSteps && typeof document.xeroExportSteps === 'object')
}

export function readXeroExportSteps(document = {}) {
  const raw = document?.xeroExportSteps
  const empty = createEmptyXeroExportSteps()
  if (!raw || typeof raw !== 'object') {
    // Infer create success from persisted Xero id (Part 14).
    if (String(document?.xeroInvoiceId || '').trim()) {
      return {
        ...empty,
        documentCreated: true,
        creditAllocation: Boolean(
          String(document?.xeroAllocatedToInvoiceId || '').trim(),
        ),
        // Legacy without step state is treated as complete elsewhere.
        complete: true,
      }
    }
    return empty
  }
  return {
    ...empty,
    ...raw,
    documentCreated:
      Boolean(raw.documentCreated) ||
      Boolean(String(document?.xeroInvoiceId || '').trim()),
    creditAllocation:
      Boolean(raw.creditAllocation) ||
      Boolean(String(document?.xeroAllocatedToInvoiceId || '').trim()),
  }
}

/**
 * Steps still needed on resume (create is never repeated once documentCreated).
 */
export function listIncompleteXeroExportSteps(
  document = {},
  {
    isCreditNote = false,
    expectSupplierAttachments = false,
    expectAuditAttachment = true,
    expectHistoryNote = true,
    expectCreditAllocation = null,
  } = {},
) {
  const steps = readXeroExportSteps(document)
  const incomplete = []

  if (!steps.documentCreated) {
    incomplete.push(XERO_EXPORT_STEPS.DOCUMENT_CREATED)
    return incomplete
  }

  if (expectSupplierAttachments && !steps.supplierAttachments) {
    incomplete.push(XERO_EXPORT_STEPS.SUPPLIER_ATTACHMENTS)
  }
  if (expectAuditAttachment && !steps.auditAttachment) {
    incomplete.push(XERO_EXPORT_STEPS.AUDIT_ATTACHMENT)
  }
  if (expectHistoryNote && !steps.historyNote) {
    incomplete.push(XERO_EXPORT_STEPS.HISTORY_NOTE)
  }

  const needAllocation =
    expectCreditAllocation == null
      ? Boolean(isCreditNote) && !steps.creditAllocationNotApplicable
      : Boolean(expectCreditAllocation)
  if (needAllocation && !steps.creditAllocation) {
    incomplete.push(XERO_EXPORT_STEPS.CREDIT_ALLOCATION)
  }

  return incomplete
}

export function isXeroExportFullyComplete(document = {}, options = {}) {
  const hasId = Boolean(String(document?.xeroInvoiceId || '').trim())
  if (!hasId) return false
  // Part 14-era docs: id present, no step ledger → already finished.
  if (!hasExplicitXeroExportSteps(document)) return true
  const steps = readXeroExportSteps(document)
  if (steps.complete === true) return true
  return listIncompleteXeroExportSteps(document, options).length === 0
}

/**
 * True when the Xero document exists but post-create work remains.
 * Resume must skip create and only retry incomplete steps.
 */
export function needsXeroExportResume(document = {}, options = {}) {
  const hasId = Boolean(String(document?.xeroInvoiceId || '').trim())
  if (!hasId) return false
  // Without an explicit step ledger, do not treat as incomplete.
  if (!hasExplicitXeroExportSteps(document)) return false
  const steps = readXeroExportSteps(document)
  if (steps.complete === true) return false
  return listIncompleteXeroExportSteps(document, options).length > 0
}

export function markXeroExportStep(steps = {}, stepName, ok, errorMessage = null) {
  const next = {
    ...createEmptyXeroExportSteps(),
    ...steps,
    updatedAt: new Date().toISOString(),
  }
  if (stepName && Object.prototype.hasOwnProperty.call(next, stepName)) {
    next[stepName] = Boolean(ok)
  }
  if (ok) {
    if (next.lastErrorStep === stepName) {
      next.lastErrorStep = null
      next.lastError = null
    }
  } else if (errorMessage) {
    next.lastErrorStep = stepName || next.lastErrorStep
    next.lastError = String(errorMessage).slice(0, 500)
  }
  return next
}

export function finaliseXeroExportSteps(steps = {}, { isCreditNote = false } = {}) {
  const next = {
    ...createEmptyXeroExportSteps(),
    ...steps,
    documentCreated: true,
    updatedAt: new Date().toISOString(),
  }
  if (!isCreditNote) {
    next.creditAllocationNotApplicable = true
    next.creditAllocation = true
  }
  const incomplete = listIncompleteXeroExportSteps(
    { xeroExportSteps: next, xeroInvoiceId: 'set' },
    {
      isCreditNote,
      expectSupplierAttachments: false,
      expectAuditAttachment: true,
      expectHistoryNote: true,
    },
  )
  next.complete = incomplete.length === 0
  return next
}
